import _ from 'lodash';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import {
  BackLink,
  Buttons,
  ClientLink,
  Currency,
  DateTime,
  DeleteButton,
  FieldControl,
  Form,
  Grid,
  Icon,
  IconButton,
  InlineTooltip,
  MultilineText,
  Page,
  Percent,
  RouteLink,
  SplitButton,
  Table,
  Tooltip,
  TooltipButton,
} from '~/components';
import { useConfirmation, useWorkspace } from '~/contexts';
import { useDocumentTitle, useFeatures, useNumberFormat } from '~/hooks';
import { colors, weights } from '~/styles';
import IssueCreditNoteDrawer from '../..//billing/credit-notes/dialogs/IssueCreditNoteDrawer';
import ProjectTags from '../components/ProjectTags';
import InvoiceAccountingActions from '../components/InvoiceAccountingActions';
import InvoiceSyncIndicator from '../components/InvoiceSyncIndicator';
import InvoiceWebLinkModal from '../invoice-actions/InvoiceWebLinkModal';
import ReloadInvoiceFromAccountingDialog from '../invoice-dialogs/ReloadInvoiceFromAccountingDialog';
import SaveInvoiceToAccountingDialog from '../invoice-dialogs/SaveInvoiceToAccountingDialog';
import UnpublishInvoiceDialog from '../invoice-dialogs/UnpublishInvoiceDialog';
import ClientPaymentForm from '../payments/ClientPaymentForm';
import ClientPaymentView from '../payments/ClientPaymentView';
import InvoiceHistoryDrawer from './InvoiceHistoryDrawer';
import LineAmountOffTooltip from './LineAmountOffTooltip';
import LineTypeTooltip from './LineTypeTooltip';
import {
  ActionsContainer,
  BilledTo,
  Footer,
  InvoiceFor,
  InvoiceNotes,
  InvoiceNotesLabel,
  LineItemsSection,
  PreviewButton,
  SaveButton,
  Separator,
  SummaryDetails,
  SummaryDetailsRow,
  SummaryGrid,
  SummarySection,
  SummaryTotal,
  Tag,
  Tags,
  TotalLabel,
  TotalRow,
  TotalValue,
  Totals,
  TotalsSeparator,
} from './StyledComponents';
import AccountingNotesTooltip from '../components/AccountingNotesTooltip';

const P = styled.p`
  white-space: pre-wrap;
`;

function HiddenColumnTooltip() {
  return (
    <Tooltip
      style={{ marginLeft: '0.5rem' }}
      message="This column will not be shown on the published version of this invoice.">
      <Icon icon="info-circle" />
    </Tooltip>
  );
}

function PublishedInvoiceView({ invoice, onChange, onDelete, onPreview, onReset, onSend }) {
  const { workspace } = useWorkspace();
  const features = useFeatures();
  const [dialog, setDialog] = useState(null);

  const documentTitle = useDocumentTitle(invoice ? `Invoice #${invoice.number}` : undefined);

  const confirmation = useConfirmation();

  const handleGetWebLink = () => {
    setDialog('webLink');
  };

  const handleViewHistory = () => {
    setDialog('history');
  };

  const handleCloseDialog = () => {
    setDialog(null);
    documentTitle.set(invoice ? `Invoice #${invoice.number}` : undefined);
  };

  const handleUnpublish = async () => {
    await confirmation.prompt((resolve) => (
      <UnpublishInvoiceDialog
        invoice={invoice}
        resolve={async () => {
          await onChange();
          resolve();
        }}
      />
    ));
  };

  const handleSend = () => {
    onSend();
  };

  const handleReceivePayment = () => {
    setDialog('payment');
  };

  const handleIssueCreditNote = () => {
    setDialog('issueCreditNote');
  };

  const history = useHistory();
  const handleCreditNoteIssued = async (creditNote) => {
    history.push(`/app/${workspace.key}/billing/credit-notes/${creditNote.id}`);
  };

  const handleSaveToAccounting = async (invoice, accountingProvider) => {
    await confirmation.prompt((resolve) => (
      <SaveInvoiceToAccountingDialog
        invoice={invoice}
        accountingProvider={accountingProvider}
        resolve={async () => {
          await onChange();
          resolve();
        }}
      />
    ));
  };

  const handleReloadFromAccounting = async (invoice, accountingProvider) => {
    await confirmation.prompt((resolve) => (
      <ReloadInvoiceFromAccountingDialog
        invoice={invoice}
        accountingProvider={accountingProvider}
        resolve={async () => {
          await onChange();
          resolve();
        }}
      />
    ));
  };

  const { nonTaxableSubtotal, taxableSubtotal, tax, total } = invoice;
  const isTaxable = invoice.lines.some((line) => line.taxable);

  const quantityFormat = useNumberFormat({ minimumFractionDigits: 0 });

  const currency = invoice.currency;

  const receivePayment = !invoice.permissions.receivePayment
    ? {
        disabled: true,
        tooltip: 'Insufficient permissions to receive a payment on this invoice.',
      }
    : !features.multicurrency && invoice.currency !== workspace.currency
      ? { disabled: true, tooltip: 'The invoice currency is different from the workspace home currency.' }
      : { disabled: false, tooltip: undefined };

  const hasPrepaymentLines = invoice.lines.some(
    (line) => line.transactionType === 'prepayment' || line.transactionType === 'prepayment_offset',
  );

  const issueCreditNote = !invoice.permissions.applyCredit
    ? {
        disabled: true,
        tooltip: 'Insufficient permissions to issue a credit note for this invoice.',
      }
    : hasPrepaymentLines
      ? {
          disabled: true,
          tooltip: 'This invoice contains prepayment lines and credit notes cannot be issued against it.',
        }
      : !features.multicurrency && invoice.currency !== workspace.currency
        ? { disabled: true, tooltip: 'The invoice currency is different from the workspace home currency.' }
        : { disabled: false, tooltip: undefined };

  const column = (name) => invoice.displayColumns.includes(name);

  return (
    <Page>
      <Page.Header>
        <BackLink defaultPath={`/app/${workspace.key}/billing/invoices`} />
        <Page.Info>
          <Page.Title>Invoice #{invoice.number}</Page.Title>
          <Tags>
            <Tag>
              <ClientLink client={invoice.client} />
            </Tag>

            {!_.isEmpty(invoice.projects) && (
              <Tag>
                <ProjectTags client={invoice.client} projects={invoice.projects} />
              </Tag>
            )}

            {features.multicurrency && <Tag>{currency}</Tag>}

            <Tag color={{ open: colors.primary, paid: colors.success }[invoice.status.id]}>
              {invoice.status.name}

              {invoice.accountingSynced && <InvoiceSyncIndicator invoice={invoice} />}
            </Tag>
            {invoice.sentAt && (
              <Tag>
                Sent on&nbsp;
                <DateTime value={invoice.sentAt} />
              </Tag>
            )}
            {invoice.status.id === 'open' && (
              <Tag>
                Open Balance of <Currency value={invoice.balance} currency={currency} />
              </Tag>
            )}

            {invoice.isLate && (
              <Tag color={colors.danger}>
                {invoice.daysLate} {invoice.daysLate === 1 ? 'Day' : 'Days'} Late
              </Tag>
            )}

            {invoice.projects.some(({ accountingNotes }) => !!accountingNotes) && (
              <Tag>
                <AccountingNotesTooltip projects={invoice.projects} />
              </Tag>
            )}
          </Tags>
        </Page.Info>

        <Page.Actions>
          <IconButton icon="history" tooltip="Invoice History" onClick={handleViewHistory} />
        </Page.Actions>
      </Page.Header>

      <Page.Section>
        <SummarySection>
          <SummaryGrid>
            <SummaryDetailsRow equalHeight>
              <Grid.Column>
                <BilledTo>
                  <dt>Bill To</dt>
                  <dl>
                    <MultilineText value={invoice.billTo} />
                  </dl>
                </BilledTo>
              </Grid.Column>
              <Grid.Column>
                <SummaryDetails>
                  {invoice.poNumber && (
                    <>
                      <dt>PO #</dt>
                      <dd>{invoice.poNumber}</dd>
                    </>
                  )}

                  {invoice.servicesThrough && (
                    <>
                      <dt>Through</dt>
                      <dd>
                        <DateTime value={invoice.servicesThrough} />
                      </dd>
                    </>
                  )}

                  <dt>Issued</dt>
                  <dd>
                    <DateTime value={invoice.issuedOn} />
                  </dd>
                  <dt>Due</dt>
                  <dd>
                    {invoice.paymentTerm?.type === 'due_on_receipt' ? 'On Receipt' : <DateTime value={invoice.dueOn} />}
                  </dd>
                </SummaryDetails>
              </Grid.Column>

              <Grid.Column>
                <SummaryTotal>
                  <FieldControl>
                    <strong>
                      <Currency value={total} currency={currency} />
                    </strong>
                  </FieldControl>
                  <small>Total Invoiced</small>
                </SummaryTotal>
              </Grid.Column>
            </SummaryDetailsRow>
            <Grid.Row>
              <Grid.Column>
                <InvoiceFor>
                  <dt>Subject</dt>
                  <dd>{invoice.invoiceFor}</dd>
                </InvoiceFor>
              </Grid.Column>
            </Grid.Row>
          </SummaryGrid>
        </SummarySection>
      </Page.Section>

      <LineItemsSection>
        <Table style={{ fontSize: '.875rem' }}>
          <Table.BoxHeader>
            <Table.Column width="15rem">Item {!column('item') && <HiddenColumnTooltip />}</Table.Column>
            <Table.Column>Details {!column('details') && <HiddenColumnTooltip />}</Table.Column>
            <Table.Column align="right" width="7rem">
              QTY {!column('quantity') && <HiddenColumnTooltip />}
            </Table.Column>
            <Table.Column align="right" width="11rem">
              Rate {!column('rate') && <HiddenColumnTooltip />}
            </Table.Column>
            <Table.Column align="right" width="10rem">
              Amount
            </Table.Column>
            <Table.Column align="center" width="4rem">
              Tax {!column('tax') && <HiddenColumnTooltip />}
            </Table.Column>
            <Table.Column align="center" width="3.5rem">
              <Icon icon="link" />
            </Table.Column>
          </Table.BoxHeader>

          {invoice.lines.map((line) => (
            <Table.BoxRow key={line.id}>
              <Table.Cell>{line.item}</Table.Cell>

              <Table.Cell>
                <div>
                  <P>{line.description}</P>
                </div>
              </Table.Cell>

              <Table.Cell align="right">{line.quantity ? quantityFormat.format(line.quantity) : ''}</Table.Cell>

              <Table.Cell align="right">
                <Currency value={line.rate} useDash={false} maximumFractionDigits={7} currency={currency} />
              </Table.Cell>

              <Table.Cell align="right">
                <Currency value={line.amount} useDash={false} currency={currency} />
                {line.transactionType && line.transactionAmount !== line.amount && (
                  <LineAmountOffTooltip line={line} currency={currency} />
                )}
              </Table.Cell>

              <Table.Cell>{line.taxable && <Icon icon="check" color={colors.success} />}</Table.Cell>

              <Table.Cell>
                <LineTypeTooltip line={line} />
              </Table.Cell>
            </Table.BoxRow>
          ))}
        </Table>

        <Separator />
      </LineItemsSection>

      <Footer>
        {invoice.notes && (
          <InvoiceNotes>
            <InvoiceNotesLabel>Note to Client</InvoiceNotesLabel>
            <P>{invoice.notes}</P>
          </InvoiceNotes>
        )}

        <Totals>
          {isTaxable && (
            <TotalRow>
              <TotalLabel>Subtotal</TotalLabel>
              <TotalValue>
                <Currency value={invoice.linesSubtotal} currency={currency} />
              </TotalValue>
            </TotalRow>
          )}

          {invoice.discountAmount > 0 && (
            <>
              <TotalRow>
                <TotalLabel>
                  Discount
                  {invoice.discountMethod === 'percentage' && _.isNumber(invoice.discountPercentage) && (
                    <>
                      {' '}
                      <Percent
                        value={invoice.discountPercentage / 100}
                        minimumFractionDigits={0}
                        maximumFractionDigits={2}
                      />
                    </>
                  )}
                </TotalLabel>
                <TotalValue>
                  <Currency value={invoice.discountAmount * -1} currency={currency} />
                </TotalValue>
              </TotalRow>

              {!isTaxable && <TotalsSeparator />}
            </>
          )}

          {isTaxable && (
            <>
              <TotalsSeparator />

              <TotalRow>
                <TotalLabel>Non-Taxable Subtotal</TotalLabel>
                <TotalValue>
                  <Currency value={nonTaxableSubtotal} currency={currency} />
                </TotalValue>
              </TotalRow>

              <TotalRow>
                <TotalLabel>Taxable Subtotal</TotalLabel>
                <TotalValue>
                  <Currency value={taxableSubtotal} currency={currency} />
                </TotalValue>
              </TotalRow>

              <TotalRow>
                <TotalLabel>
                  Tax{' '}
                  {invoice.taxRate !== null && (
                    <Percent value={invoice.taxRate / 100} minimumFractionDigits={0} maximumFractionDigits={7} />
                  )}
                </TotalLabel>
                <TotalValue>
                  <Currency value={tax} currency={currency} />
                </TotalValue>
              </TotalRow>

              <TotalsSeparator />
            </>
          )}

          <TotalRow style={{ fontWeight: weights.bold }}>
            <TotalLabel>Total Invoiced</TotalLabel>
            <TotalValue>
              <Currency value={total} currency={currency} />
            </TotalValue>
          </TotalRow>
          {invoice.creditNotes?.length > 0 &&
            invoice.creditNotes.map(({ amount, creditNoteId }) => {
              return (
                <TotalRow key={creditNoteId} data-testid={creditNoteId}>
                  <TotalLabel>Applied Credit</TotalLabel>
                  <TotalValue>
                    <RouteLink to={`/app/${workspace.key}/billing/credit-notes/${creditNoteId}`}>
                      <Currency value={amount} currency={currency} />
                    </RouteLink>
                  </TotalValue>
                </TotalRow>
              );
            })}
          {invoice.payments?.length > 0 &&
            invoice.payments.map(({ amount, clientPaymentId }) => {
              function handleViewPayment() {
                setDialog({ type: 'viewPayment', clientPaymentId });
              }
              return (
                <TotalRow key={clientPaymentId} data-testid={clientPaymentId}>
                  <TotalLabel>Payment Received</TotalLabel>
                  <TotalValue clickable onClick={handleViewPayment}>
                    <Currency value={amount} currency={currency} />
                  </TotalValue>
                </TotalRow>
              );
            })}
          <TotalRow style={{ fontWeight: weights.bold }}>
            <TotalLabel>Balance Due</TotalLabel>
            <TotalValue>
              <Currency value={invoice.balance} currency={currency} />
            </TotalValue>
          </TotalRow>
        </Totals>
      </Footer>

      <Form.Actions>
        <TooltipButton
          component={DeleteButton}
          disabled={
            !invoice.permissions.manage || invoice.hasPayments || invoice.hasCreditNotes || invoice.hasBilledPrepayments
          }
          tooltip={
            !invoice.permissions.manage
              ? 'Insufficient permissions to delete this invoice'
              : invoice.hasPayments
                ? 'An invoice with received payments cannot be deleted.'
                : invoice.hasCreditNotes
                  ? 'An invoice with applied credit notes cannot be deleted.'
                  : invoice.hasBilledPrepayments
                    ? 'An invoice with prepayments applied to other invoices cannot be deleted.'
                    : undefined
          }
          onClick={onDelete}>
          Delete
        </TooltipButton>

        <ActionsContainer>
          <Buttons>
            <PreviewButton onClick={onPreview}>Preview</PreviewButton>

            <SplitButton>
              {
                {
                  open: invoice.sentAt ? (
                    <SaveButton disabled={receivePayment.disabled} onClick={handleReceivePayment}>
                      Receive Payment
                      {receivePayment.tooltip && <InlineTooltip message={receivePayment.tooltip} />}
                    </SaveButton>
                  ) : (
                    <SaveButton disabled={!invoice.permissions.manage} onClick={handleSend}>
                      Send
                      {!invoice.permissions.manage && (
                        <InlineTooltip message="Insufficient permissions to send this invoice." />
                      )}
                    </SaveButton>
                  ),

                  paid: (
                    <SaveButton onClick={handleSend} disabled={!invoice.permissions.manage}>
                      Send
                      {!invoice.permissions.manage && (
                        <InlineTooltip message="Insufficient permissions to send this invoice." />
                      )}
                    </SaveButton>
                  ),
                }[invoice.status.id]
              }

              <SplitButton.Menu position="top" data-testid="invoice_actions">
                {({ setIsOpen }) => {
                  const handleAction = async (action) => {
                    setIsOpen(false);
                    await action();
                  };

                  return {
                    open: (
                      <>
                        <SplitButton.Item
                          onClick={handleSend}
                          disabled={!invoice.permissions.manage}
                          tooltip={
                            !invoice.permissions.manage ? 'Insufficient permissions to send this invoice.' : undefined
                          }>
                          Send
                        </SplitButton.Item>

                        <SplitButton.Item
                          onClick={handleReceivePayment}
                          disabled={receivePayment.disabled}
                          tooltip={receivePayment.tooltip}>
                          Receive Payment
                        </SplitButton.Item>

                        <SplitButton.Item
                          onClick={handleIssueCreditNote}
                          disabled={issueCreditNote.disabled}
                          tooltip={issueCreditNote.tooltip}>
                          Issue Credit Note
                        </SplitButton.Item>

                        <SplitButton.Item
                          disabled={
                            invoice.hasPayments ||
                            invoice.hasCreditNotes ||
                            invoice.hasBilledPrepayments ||
                            !invoice.permissions.manage
                          }
                          tooltip={
                            !invoice.permissions.manage
                              ? 'Insufficient permissions to unpublish this invoice.'
                              : invoice.hasPayments
                                ? 'An invoice with received payments cannot be unpublished.'
                                : invoice.hasCreditNotes
                                  ? 'An invoice with applied credit notes cannot be unpublished.'
                                  : invoice.hasBilledPrepayments
                                    ? 'An invoice with prepayments applied to other invoices cannot be unpublished.'
                                    : undefined
                          }
                          onClick={() => handleAction(handleUnpublish)}>
                          Unpublish
                        </SplitButton.Item>

                        <SplitButton.Item onClick={handleGetWebLink}>Get Web Link</SplitButton.Item>

                        <InvoiceAccountingActions
                          accountingSynced={invoice.accountingSynced}
                          accountingProvider={invoice.accountingProvider}
                          disabled={!invoice.permissions.manage}
                          tooltip={
                            !invoice.permissions.manage ? 'Insufficient permissions to manage this invoice.' : undefined
                          }
                          Container={SplitButton.Item}
                          onSave={(accountingProvider) =>
                            handleAction(() => handleSaveToAccounting(invoice, accountingProvider))
                          }
                          onReload={(accountingProvider) =>
                            handleAction(() => handleReloadFromAccounting(invoice, accountingProvider))
                          }
                        />
                      </>
                    ),

                    paid: (
                      <>
                        <SplitButton.Item
                          onClick={handleSend}
                          disabled={!invoice.permissions.manage}
                          tooltip={
                            !invoice.permissions.manage ? 'Insufficient permissions to send this invoice.' : undefined
                          }>
                          Send
                        </SplitButton.Item>

                        <SplitButton.Item
                          disabled={
                            invoice.hasPayments ||
                            invoice.hasCreditNotes ||
                            invoice.hasBilledPrepayments ||
                            !invoice.permissions.manage
                          }
                          tooltip={
                            !invoice.permissions.manage
                              ? 'Insufficient permissions to unpublish this invoice.'
                              : invoice.hasPayments
                                ? 'An invoice with received payments cannot be unpublished.'
                                : invoice.hasCreditNotes
                                  ? 'An invoice with applied credit notes cannot be unpublished.'
                                  : invoice.hasBilledPrepayments
                                    ? 'An invoice with prepayments applied to other invoices cannot be unpublished.'
                                    : undefined
                          }
                          onClick={() => handleAction(handleUnpublish)}>
                          Unpublish
                        </SplitButton.Item>

                        <SplitButton.Item onClick={handleGetWebLink}>Get Web Link</SplitButton.Item>

                        <InvoiceAccountingActions
                          accountingSynced={invoice.accountingSynced}
                          accountingProvider={invoice.accountingProvider}
                          disabled={!invoice.permissions.manage}
                          tooltip={
                            !invoice.permissions.manage ? 'Insufficient permissions to manage this invoice.' : undefined
                          }
                          Container={SplitButton.Item}
                          onSave={(accountingProvider) =>
                            handleAction(() => handleSaveToAccounting(invoice, accountingProvider))
                          }
                          onReload={(accountingProvider) =>
                            handleAction(() => handleReloadFromAccounting(invoice, accountingProvider))
                          }
                        />
                      </>
                    ),
                  }[invoice.status.id];
                }}
              </SplitButton.Menu>
            </SplitButton>
          </Buttons>
        </ActionsContainer>
      </Form.Actions>

      {
        {
          webLink: <InvoiceWebLinkModal invoice={invoice} onClose={handleCloseDialog} />,
          payment: (
            <ClientPaymentForm
              invoiceId={invoice.id}
              onSaved={onReset}
              onClose={handleCloseDialog}
              onDeleted={handleCloseDialog}
            />
          ),
          viewPayment: (
            <ClientPaymentView paymentId={dialog?.clientPaymentId} onClose={handleCloseDialog} onDeleted={onReset} />
          ),
          issueCreditNote: (
            <IssueCreditNoteDrawer
              invoiceId={invoice.id}
              onSaved={handleCreditNoteIssued}
              onClose={handleCloseDialog}
            />
          ),
          history: <InvoiceHistoryDrawer invoiceId={invoice.id} onClose={handleCloseDialog} />,
        }[dialog?.type ?? dialog]
      }
    </Page>
  );
}

export default PublishedInvoiceView;
